import React, { useState, useContext } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Box, LinearProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { AddUserModal, CommonCardList, Header } from './particles';
import { callGetTeamsList, callGetTotalUsers } from '@api';
import { ApplicationContext } from '@particles';
import { Alert, TextView, Button } from '@atoms';
import { AddCompanyTeam, CompanyTeam, TeamTable } from '@organisms';

const Team: React.FC<RouteComponentProps> = () => {
  const {
    state: { user }
  } = useContext(ApplicationContext);

  const [refresh, setRefresh] = useState<number>(0);
  const [openAddUser, setOpenAddUser] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent>();
  const [alert, setAlert] = useState<
    { title: string; message: string; type: 'success' | 'error' } | undefined
  >(undefined);

  const [teamList, setTeamList] = useState<Team[]>([]);
  const [selectedTeamForFilter, setSelectedTeamForFilter] = useState<Team>();
  const [openAddTeam, setOpenAddTeam] = useState<boolean>(false);
  const [selectedTeam, setSelectedTeam] = useState<Team>();

  const totalUsersQuery = useQuery({
    queryKey: ['admin-team/total-users', user?.companyId, refresh],
    queryFn: () => callGetTotalUsers({ companyId: user?.companyId }),
    enabled: !!user?.companyId
  });

  const loading = totalUsersQuery.isFetching;
  const activeInactive: ActiveInactive = totalUsersQuery.data?.code === 200 && totalUsersQuery.data.data
    ? totalUsersQuery.data.data.data.total
    : { active: 0, inactive: 0 };

  // Bumping `refresh` re-invalidates the cards above and propagates to
  // TeamTable / CompanyTeam (they depend on it in their own deps array).
  const bumpRefresh = () => setRefresh(Date.now());

  if (!user) {
    return <div>User not found</div>;
  }

  const teamApi = () => {
    if (user.companyId) {
      callGetTeamsList({ companyId: user.companyId }).then((res) => {
        setTeamList(res.data ?? []);
      });
    }
  };

  return (
    <Box sx={{ p: 3, height: 'inherit' }}>
      {loading && <LinearProgress />}
      <Header
        companyId={user.companyId}
        onAddSuccess={() => {
          bumpRefresh();
          setAlert({ title: 'Success', message: 'User invited successfully.', type: 'success' });
          setTimeout(() => {
            setAlert(undefined);
          }, 3000);
        }}
        onUploadSuccess={() => {
          setAlert({ title: 'Success', message: 'Users invited successfully.', type: 'success' });
          setTimeout(() => {
            setAlert(undefined);
          }, 3000);
          bumpRefresh();
        }}
        onTeamChange={(team?: Team) => {
          setSelectedTeamForFilter(team);
        }}
        teamList={teamList}
      />

      {alert && <Alert severity={alert.type} title={alert.title} message={alert.message} />}
      <CommonCardList
        totalUser={activeInactive.active + activeInactive.inactive}
        activeUser={activeInactive.active}
        inactiveUser={activeInactive.inactive}
      />
      <TeamTable
        refresh={refresh}
        user={user}
        selectedTeam={selectedTeamForFilter}
        companyId={user.companyId}
        onEdit={(agent: Agent) => {
          setSelectedAgent(agent);
          setOpenAddUser(true);
        }}
      />

      <AddUserModal
        open={openAddUser}
        selectedAgent={selectedAgent}
        onClose={() => {
          setOpenAddUser(false);
          setSelectedAgent(undefined);
        }}
        onSuccess={() => {
          setOpenAddUser(false);
          setSelectedAgent(undefined);
          setRefresh(Date.now());
        }}
      />

      <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center', mt: 2 }}>
        <TextView display={'initial'} $fontSize={30}>
          All Teams
        </TextView>
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => setOpenAddTeam(true)}>{'Add Team'}</Button>
      </Box>

      <CompanyTeam
        companyId={user.companyId}
        refresh={refresh}
        isSuperAdmin={true}
        onEdit={(team: Team) => {
          setSelectedTeam(team);
          setOpenAddTeam(true);
        }}
      />

      <AddCompanyTeam
        open={openAddTeam}
        selectedTeam={selectedTeam}
        companyId={user.companyId}
        onClose={() => {
          setOpenAddTeam(false);
          setSelectedTeam(undefined);
        }}
        onSuccess={() => {
          setOpenAddTeam(false);
          setSelectedTeam(undefined);
          teamApi();
          setRefresh(Date.now());
        }}
      />
    </Box>
  );
};

export default Team;
